import { observer } from 'mobx-react';
import { FC, Fragment, useMemo, useState } from 'react';

import { Button, Combobox, Flex, Text } from '@servicetitan/anvil2';
import { provide, useDependencies } from '@servicetitan/react-ioc';

import { BottomDrawerMultiSelect } from '../../components/bottom-drawer-multi-select/bottom-drawer-multi-select';
import { EstimateViewModel } from '../models/estimate-view.model';
import { EstimateSelectStore } from '../stores/estimate-select.store';

export interface EstimateSelect {
    onSubmit: (estimates: EstimateViewModel[]) => void;
}

export const EstimateSelect: FC<EstimateSelect> = provide({ singletons: [] })(
    observer(({ onSubmit }) => {
        const [estimateSelectStore] = useDependencies<[EstimateSelectStore]>(EstimateSelectStore);

        const [drawerOpen, setDrawerOpen] = useState(false);
        const [searchText, setSearchText] = useState('');

        const [selectedOemPart, setSelectedOemPart] = useState<EstimateViewModel[]>([]);

        const options = useMemo(() => {
            const items = estimateSelectStore.estimates;
            const query = searchText.toLowerCase();
            return items.filter(e => e.label?.toLowerCase().includes(query));
        }, [estimateSelectStore.estimates, searchText]);

        const handleSubmit = () => {
            if (selectedOemPart) {
                onSubmit(selectedOemPart);
            }
        };

        return (
            <Fragment>
                <Flex direction="column" gap="3">
                    <Text size="small">Select estimate(s) to add this part to</Text>
                    <div onClick={() => setDrawerOpen(true)}>
                        <Combobox
                            multiple
                            open={false}
                            items={selectedOemPart}
                            selectedItems={selectedOemPart}
                            onChange={(items: EstimateViewModel[]) => {
                                setSelectedOemPart(items);
                                setDrawerOpen(false);
                            }}
                            itemToString={item => item?.label ?? ''}
                            itemToKey={item => (item ? item.id : null)}
                        >
                            <Combobox.SelectTrigger placeholder="Select option(s)" />
                        </Combobox>
                    </div>
                    <Flex>
                        <Button
                            appearance="primary"
                            disabled={!selectedOemPart}
                            loading={estimateSelectStore.isLoading}
                            onClick={handleSubmit}
                        >
                            Submit
                        </Button>
                    </Flex>
                </Flex>
                <BottomDrawerMultiSelect
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    dropdownOptions={options}
                    itemToKey={item => item.id}
                    searchPlaceholder="Search estimates"
                    searchInputInfo="Begin typing to view matches"
                    onSearchQueryChange={setSearchText}
                    onChange={item => {
                        setSelectedOemPart(item);
                        setDrawerOpen(false);
                    }}
                    values={selectedOemPart}
                    itemRenderer={item => {
                        return (
                            <Flex>
                                <Text>
                                    {item.label && item.label.length > 25
                                        ? `${item.label.slice(0, 25)}...`
                                        : item.label}
                                </Text>
                            </Flex>
                        );
                    }}
                />
            </Fragment>
        );
    })
);
